// components/Sender/TemplateEditor.js
import React, { useState } from "react";
import { SenderApi } from "./SenderApi";
import {
  campaignChannel, campaignPlatformSet, campaignName,
  workersForChannels, ChannelBadge,
} from "./channels";
import {
  WORKER_KIND, WORKER_TYPE,
  buildWorkerUsageIndex, findOtherUsages,
  workerGroupKey,
} from "./workers";
import { BulkWorkersDialog } from "./BulkWorkersDialog";
import { TemplateBulkRuntimeDialog } from "./TemplateBulkRuntimeDialog";

// ═══════════════════════════════════════════════════════════════
// Нормализация каналов
// ═══════════════════════════════════════════════════════════════
const _CHANNEL_ALIASES = {
  "EMAIL": "EMAIL", "E-MAIL": "EMAIL", "E_MAIL": "EMAIL",
  "MAIL": "EMAIL", "EMAIL_ONLY": "EMAIL",
  "CONTACT": "CONTACT_FORM", "CF": "CONTACT_FORM",
  "CONTACT_FORM": "CONTACT_FORM", "CONTACT_FORM_ONLY": "CONTACT_FORM",
  "CALENDLY": "CALENDLY", "CALENDLY_ONLY": "CALENDLY",
  "EMAIL_AND_CONTACT": "EMAIL_AND_CONTACT",
  "CONTACT_AND_EMAIL": "EMAIL_AND_CONTACT",
  "EMAILANDCONTACT": "EMAIL_AND_CONTACT",
  "CONTACTANDEMAIL": "EMAIL_AND_CONTACT",
  "BOTH": "EMAIL_AND_CONTACT",
};
function _normChannel(raw) {
  const s = String(raw || "").trim().toUpperCase();
  if (!s) return "EMAIL";
  return _CHANNEL_ALIASES[s] || s;
}
function _toBackendChannel(raw) {
  return _normChannel(raw);
}

// EMAIL_AND_CONTACT наследует EMAIL (email-шаг первый).
function _runtimeKeyFor(rawChannel) {
  const ch = _normChannel(rawChannel);
  if (ch === "CONTACT_FORM") return "CONTACT_FORM";
  if (ch === "CALENDLY") return "CALENDLY";
  return "EMAIL";
}

function _channelMeta(ch) {
  if (ch === "EMAIL") return { icon: "📧", label: "EMAIL" };
  if (ch === "CONTACT_FORM") return { icon: "📝", label: "CONTACT_FORM" };
  if (ch === "CALENDLY") return { icon: "📅", label: "CALENDLY" };
  if (ch === "EMAIL_AND_CONTACT") return { icon: "✉️", label: "EMAIL + CONTACT" };
  return { icon: "❔", label: ch };
}

function _modeChannels(mode) {
  if (mode === "EMAIL_ONLY") return ["EMAIL"];
  if (mode === "CONTACT_FORM_ONLY") return ["CONTACT_FORM"];
  if (mode === "CALENDLY_ONLY") return ["CALENDLY"];
  if (mode === "BOTH") return ["EMAIL", "CONTACT_FORM"];
  return [];
}

const _MODE_META = {
  EMAIL_ONLY:        { icon: "📧", label: "Email" },
  CONTACT_FORM_ONLY: { icon: "📝", label: "Contact form" },
  CALENDLY_ONLY:     { icon: "📅", label: "Calendly" },
  BOTH:              { icon: "✉️", label: "Email + Contact" },
};

// ═══════════════════════════════════════════════════════════════
// TemplateEditor
// ═══════════════════════════════════════════════════════════════
export function TemplateEditor({
  template, segments, workers, campaigns, runtimes, workersError,
  onSave, onCancel, onRefreshWorkers, add,
}) {
  const isNew = !template?.id;
  const [draft, setDraft] = useState(() => ({
    name: template?.name || "Новый шаблон",
    total_limit: template?.total_limit || 0,
    pause_min: template?.pause_min || 0,
    pause_max: template?.pause_max || 0,
    test_run: !!template?.test_run,
    test_fill_cf: template?.test_fill_cf !== false,
    segment_blocks: (template?.segment_blocks || []).map((b) => ({
      ...b,
      runtime_defaults: { ...(b.runtime_defaults || {}) },
      workers: (b.workers || []).map((w) => ({ ...w })),
    })),
  }));
  const [editingBlock, setEditingBlock] = useState(null);
  const [bulkRuntimeOpen, setBulkRuntimeOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const patch = (p) => setDraft({ ...draft, ...p });
  const blocks = draft.segment_blocks || [];
  const patchBlocks = (next) => patch({ segment_blocks: next });

  const addBlock = () => setEditingBlock({ mode: "new" });
  const editBlock = (idx) => setEditingBlock({ mode: "edit", idx });
  const deleteBlock = (idx) => {
    if (!window.confirm("Удалить блок?")) return;
    const next = blocks.slice();
    next.splice(idx, 1);
    patchBlocks(next);
  };
  const dupBlock = (idx) => {
    const next = blocks.slice();
    next.splice(idx + 1, 0, JSON.parse(JSON.stringify(blocks[idx])));
    patchBlocks(next);
  };

  const onBlockSaved = (blk) => {
    if (editingBlock.mode === "new") patchBlocks(blocks.concat([blk]));
    else {
      const next = blocks.slice();
      next[editingBlock.idx] = blk;
      patchBlocks(next);
    }
    setEditingBlock(null);
  };

  const onBulkRuntimeApply = (changes) => {
    const byBlock = {};
    changes.forEach((c) => {
      if (!byBlock[c.blockIdx]) byBlock[c.blockIdx] = {};
      byBlock[c.blockIdx][c.workerName] = c.runtimeId;
    });
    const next = blocks.map((b, idx) => {
      const updates = byBlock[idx];
      if (!updates) return b;
      return {
        ...b,
        workers: (b.workers || []).map((w) => {
          if (Object.prototype.hasOwnProperty.call(updates, w.worker_name)) {
            return { ...w, runtime_id: updates[w.worker_name] };
          }
          return w;
        }),
      };
    });
    patchBlocks(next);
    add?.(`🎯 Runtime назначен для ${changes.length} воркеров`, "success");
  };

  const save = async () => {
    if (!draft.name?.trim()) { alert("Укажи название"); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        segment_blocks: blocks.map((b) => ({
          segment_id: b.segment_id,
          limit: b.limit || 0,
          limit_mode: b.limit_mode || "abs",
          mode: b.mode || "BOTH",
          cf_selection: b.cf_selection || "random",
          cf_failed_action: b.cf_failed_action || "send_email",
          fetch_mode: b.fetch_mode || "sequential",
          runtime_defaults: b.runtime_defaults || {},
          workers: (b.workers || []).map((w) => ({
            worker_name: w.worker_name,
            percent: w.percent || 100,
            limit: w.limit || 0,
            limit_mode: w.limit_mode || "abs",
            channel: _toBackendChannel(w.channel),
            runtime_id: w.runtime_id || null,
          })),
        })),
        total_limit: draft.total_limit || 0,
        pause_min: draft.pause_min || 0,
        pause_max: draft.pause_max || 0,
        test_run: !!draft.test_run,
        test_fill_cf: draft.test_fill_cf !== false,
      };
      let saved;
      if (isNew) saved = await SenderApi.createTemplate(payload);
      else saved = await SenderApi.updateTemplate({ ...payload, id: template.id });
      add?.(`💾 Шаблон «${saved.name}» сохранён`, "success");
      onSave(saved);
    } catch (e) {
      add?.("Ошибка сохранения: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const totalWorkers = blocks.reduce((s, b) => s + (b.workers?.length || 0), 0);

  return (
    <div style={overlay} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={header}>
          <div>
            <h3 style={{ margin: 0 }}>
              {isNew ? "➕ Новый шаблон" : `✏️ Редактор «${template.name}»`}
            </h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              {isNew ? "Соберите блоки и сохраните." : `id: ${template.id}`}
              {" · "}
              <span style={{ color: workers.length ? "#16a34a" : "#dc2626" }}>
                Воркеров в системе: {workers.length}
              </span>
              {workersError && (
                <span style={{ color: "#dc2626" }}> · ⚠️ {workersError}</span>
              )}
            </div>
          </div>
          <button className="btn-cancel" onClick={onCancel}>✕</button>
        </div>

        <div style={body}>
          {/* Шапка шаблона */}
          <div style={{ display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                        gap: 12, alignItems: "end" }}>
            <div>
              <label className="form-label">Название</label>
              <input className="form-input" value={draft.name}
                     onChange={(e) => patch({ name: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Общий лимит</label>
              <input className="form-input" type="number" min={0}
                     value={draft.total_limit}
                     onChange={(e) =>
                       patch({ total_limit: parseInt(e.target.value, 10) || 0 })} />
            </div>
            <div>
              <label className="form-label">Пауза min, сек</label>
              <input className="form-input" type="number" min={0} step={0.1}
                     value={draft.pause_min}
                     onChange={(e) =>
                       patch({ pause_min: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="form-label">Пауза max, сек</label>
              <input className="form-input" type="number" min={0} step={0.1}
                     value={draft.pause_max}
                     onChange={(e) =>
                       patch({ pause_max: parseFloat(e.target.value) || 0 })} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6,
                          paddingBottom: 4 }}>
              <label style={checkLabel}>
                <input type="checkbox" checked={draft.test_run}
                       onChange={(e) => patch({ test_run: e.target.checked })} />
                🧪 test_run
              </label>
              <label style={checkLabel}>
                <input type="checkbox" checked={draft.test_fill_cf}
                       disabled={!draft.test_run}
                       onChange={(e) =>
                         patch({ test_fill_cf: e.target.checked })} />
                fill CF
              </label>
            </div>
          </div>

          {/* Панель действий */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", gap: 8, flexWrap: "wrap",
            padding: "10px 14px", background: "#f9fafb",
            borderRadius: 10, border: "1px solid #e5e7eb",
          }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              📦 Блоки ({blocks.length})
              <span style={{ fontWeight: 400, color: "#6b7280",
                             fontSize: 12, marginLeft: 8 }}>
                · воркеров в шаблоне: {totalWorkers}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn-cancel"
                      style={{ color: "#0891b2", borderColor: "#bae6fd" }}
                      onClick={() => setBulkRuntimeOpen(true)}
                      disabled={totalWorkers === 0}
                      title={totalWorkers === 0
                        ? "В шаблоне нет воркеров"
                        : "Массово назначить runtime всем воркерам шаблона"}>
                🎯 Массово runtime
              </button>
              <button className="btn-cancel" onClick={onRefreshWorkers}>
                🔄 Воркеры
              </button>
              <button className="btn-primary" onClick={addBlock}
                      disabled={!segments?.length}>
                ➕ Добавить блок
              </button>
            </div>
          </div>

          {/* Список блоков */}
          {blocks.length === 0 ? (
            <p style={{ color: "#9ca3af", fontSize: 13 }}>Пока нет блоков.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {blocks.map((b, idx) => {
                const seg = segments.find((s) => s.id === b.segment_id);
                const wArr = b.workers || [];
                const rtDefaults = b.runtime_defaults || {};
                const modeMeta = _MODE_META[b.mode] || { icon: "?", label: b.mode };
                const defParts = Object.entries(rtDefaults)
                  .filter(([, v]) => v)
                  .map(([k, v]) => `${_channelMeta(k).icon} ${v}`);
                return (
                  <div key={b.id || idx} style={blockCard}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4,
                                    display: "flex", alignItems: "center",
                                    gap: 8, flexWrap: "wrap" }}>
                        #{idx + 1} · {seg?.name || `⚠️ ${b.segment_id}`}
                        <span style={{ fontSize: 11, fontWeight: 400,
                                       color: "#0891b2" }}>
                          {modeMeta.icon} {modeMeta.label}
                        </span>
                        {seg?.filters?.campaign_id?.map((cid) => {
                          const ch = campaignChannel(campaigns?.[String(cid)]);
                          return ch
                            ? <ChannelBadge key={cid} channel={ch} size="sm" />
                            : null;
                        })}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        Лимит: {b.limit_mode === "segment_percent"
                          ? `${b.limit}% сегм.` : (b.limit || "∞")}
                        {" · "} Воркеров: {wArr.length}
                        {defParts.length > 0 && (
                          <span style={{ marginLeft: 8, color: "#0891b2" }}>
                            · 🖥 {defParts.join(" ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn-cancel" style={iconBtn}
                              onClick={() => editBlock(idx)}>✏️</button>
                      <button className="btn-cancel" style={iconBtn}
                              onClick={() => dupBlock(idx)}>📋</button>
                      <button className="btn-cancel"
                              style={{ ...iconBtn, color: "#dc2626" }}
                              onClick={() => deleteBlock(idx)}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onCancel}>Отмена</button>
          <button className="btn-primary" disabled={saving} onClick={save}>
            {saving ? "⏳ Сохранение…" : (isNew ? "💾 Создать" : "💾 Сохранить")}
          </button>
        </div>
      </div>

      {editingBlock && (
        <BlockDialog
          initial={editingBlock.mode === "edit"
            ? blocks[editingBlock.idx]
            : newEmptyBlock()}
          blockIdx={editingBlock.mode === "edit" ? editingBlock.idx : -1}
          template={draft}
          segments={segments}
          workers={workers}
          campaigns={campaigns}
          runtimes={runtimes || []}
          workersError={workersError}
          onRefreshWorkers={onRefreshWorkers}
          onSave={onBlockSaved}
          onCancel={() => setEditingBlock(null)}
        />
      )}

      {bulkRuntimeOpen && (
        <TemplateBulkRuntimeDialog
          blocks={blocks}
          runtimes={runtimes || []}
          onApply={onBulkRuntimeApply}
          onClose={() => setBulkRuntimeOpen(false)}
        />
      )}
    </div>
  );
}

function newEmptyBlock() {
  return {
    segment_id: "", limit: 0, limit_mode: "abs",
    mode: "BOTH",
    cf_selection: "random",
    cf_failed_action: "send_email",
    fetch_mode: "sequential",
    runtime_defaults: {},
    workers: [],
  };
}

// ═══════════════════════════════════════════════════════════════
// BlockDialog
// ═══════════════════════════════════════════════════════════════
function BlockDialog({
  initial, blockIdx, template, segments, workers, campaigns, runtimes,
  workersError, onRefreshWorkers, onSave, onCancel,
}) {
  const [blk, setBlk] = useState(() => ({
    segment_id: "",
    limit: 0,
    limit_mode: "abs",
    mode: "BOTH",
    cf_selection: "random",
    cf_failed_action: "send_email",
    fetch_mode: "sequential",
    runtime_defaults: {},
    ...initial,
    runtime_defaults: { ...(initial?.runtime_defaults || {}) },
    workers: (initial?.workers || []).map((w) => ({ ...w })),
  }));
  const [bulkOpen, setBulkOpen] = useState(false);
  const [evenDistribution, setEvenDistribution] = useState(false);

  const patch = (p) => setBlk({ ...blk, ...p });
  const patchRtDefaults = (channel, rtId) => {
    const next = { ...blk.runtime_defaults };
    if (rtId) next[channel] = rtId;
    else delete next[channel];
    patch({ runtime_defaults: next });
  };

  const recalcEven = (list) => {
    if (!list || list.length === 0) return list;
    const pct = Math.round((100 / list.length) * 100) / 100;
    return list.map((w) => ({ ...w, percent: pct }));
  };
  const applyEvenIfNeeded = (list) =>
    evenDistribution ? recalcEven(list) : list;

  const normalizeTo100 = () => {
    const sum = blk.workers.reduce((s, w) => s + (Number(w.percent) || 0), 0);
    if (sum <= 0) { patch({ workers: recalcEven(blk.workers) }); return; }
    const next = blk.workers.map((w) => ({
      ...w,
      percent: Math.round(((Number(w.percent) || 0) * 100 / sum) * 100) / 100,
    }));
    patch({ workers: next });
  };
  const toggleEvenDistribution = (checked) => {
    setEvenDistribution(checked);
    if (checked && blk.workers.length > 0) {
      patch({ workers: recalcEven(blk.workers) });
    }
  };

  // ─── Данные сегмента ───
  const selectedSeg = segments.find((s) => s.id === blk.segment_id);
  const segCampaignIds = selectedSeg?.filters?.campaign_id || [];
  const segChannels = (() => {
    const set = new Set();
    segCampaignIds.forEach((cid) => {
      const c = campaigns?.[String(cid)];
      if (c) campaignPlatformSet(c).forEach((ch) => set.add(ch));
    });
    return set;
  })();

  const availableWorkers = workersForChannels(workers, segChannels);
  const inBlockNames = new Set(blk.workers.map((w) => w.worker_name));

  // Воркеры из ДРУГИХ блоков шаблона — для исключения из bulk-add.
  // Текущий блок исключён — его blk.workers уже в inBlockNames и могут
  // меняться в процессе редактирования.
  const usedInOtherBlocks = (() => {
    const s = new Set();
    (template.segment_blocks || []).forEach((b, idx) => {
      if (idx === blockIdx) return;
      (b.workers || []).forEach((w) => s.add(w.worker_name));
    });
    return s;
  })();

  // ─── Индекс использований (по ID) ───
  const segById = (() => {
    const m = {};
    (segments || []).forEach((s) => { m[s.id] = s; });
    return m;
  })();
  const usageIndex = buildWorkerUsageIndex(
    template.segment_blocks || [],
    segById,
  );

  const totalPct = blk.workers.reduce((s, w) => s + (Number(w.percent) || 0), 0);
  const blockMessages = blk.limit_mode === "abs" && blk.limit > 0
    ? blk.limit : null;
  const estPerWorker = (w) => blockMessages && w.percent
    ? Math.round((w.percent * blockMessages) / 100) : null;

  const onBulkAdd = (newWorkers, pct) => {
    // Занятые group keys: текущий блок + все другие блоки шаблона.
    const usedKeys = new Set();
    blk.workers.forEach((w) => usedKeys.add(workerGroupKey(w.worker_name)));
    usedInOtherBlocks.forEach((n) => usedKeys.add(workerGroupKey(n)));

    const toAppend = [];
    newWorkers.forEach((w) => {
      const key = workerGroupKey(w._name);
      if (usedKeys.has(key)) return;
      usedKeys.add(key);
      toAppend.push({
        worker_name: w._name,
        percent: pct,
        limit: 0,
        limit_mode: "abs",
        channel: w._type || "email",
        runtime_id: null,
      });
    });
    if (!toAppend.length) return;
    const combined = [...blk.workers, ...toAppend];
    patch({ workers: applyEvenIfNeeded(combined) });
  };

  const addOne = () => {
    if (!availableWorkers.length) {
      alert("Нет доступных воркеров.\n\n" + (workersError || "Проверь список"));
      return;
    }
    const w = availableWorkers[0];
    const nm = w._name || w.name;
    if (inBlockNames.has(nm)) { alert("Этот воркер уже в блоке"); return; }
    const combined = [...blk.workers, {
      worker_name: nm, percent: 100, limit: 0, limit_mode: "abs",
      channel: w._type || "email", runtime_id: null,
    }];
    patch({ workers: applyEvenIfNeeded(combined) });
  };

  const removeWorker = (idx) => {
    const list = blk.workers.slice();
    list.splice(idx, 1);
    patch({ workers: applyEvenIfNeeded(list) });
  };

  const clearAll = () => {
    if (!blk.workers.length) return;
    if (!window.confirm(
      `Удалить всех воркеров из блока (${blk.workers.length})?`)) return;
    patch({ workers: [] });
  };

  const setWorkerField = (idx, field, value) => {
    const list = blk.workers.slice();
    list[idx] = { ...list[idx], [field]: value };
    patch({ workers: list });
  };

  const workerByName = {};
  availableWorkers.forEach((w) => { workerByName[w._name || w.name] = w; });
  const availableNames = new Set(Object.keys(workerByName));

  const pctStatus = totalPct === 100 ? "ok"
    : totalPct > 100 ? "over" : "under";
  const pctStatusColor = {
    ok:    { bg: "#f0fdf4", border: "#bbf7d0", text: "#16a34a", label: "✓ норма" },
    over:  { bg: "#fef2f2", border: "#fecaca", text: "#dc2626", label: "⚠️ > 100%" },
    under: { bg: "#fef9c3", border: "#fde68a", text: "#b45309", label: "⚠️ < 100%" },
  }[pctStatus];

  const evenPct = blk.workers.length > 0
    ? Math.round((100 / blk.workers.length) * 100) / 100 : 0;

  const modeChannels = _modeChannels(blk.mode);
  const runtimeChoices = (runtimes || []).filter((r) => r.is_enabled !== false);

  return (
    <div style={{ ...overlay, zIndex: 1300 }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ ...card, maxWidth: 1180 }}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>🧩 Редактор блока</h3>
          <button className="btn-cancel" onClick={onCancel}>✕</button>
        </div>

        <div style={body}>
          {/* Сегмент + режим */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr",
                        gap: 12 }}>
            <div>
              <label className="form-label">Сегмент</label>
              <select className="form-input" value={blk.segment_id}
                      onChange={(e) =>
                        patch({ segment_id: e.target.value, workers: [] })}>
                <option value="">— выбери сегмент —</option>
                {segments.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {selectedSeg && segCampaignIds.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", gap: 8,
                              flexWrap: "wrap", alignItems: "center",
                              fontSize: 12 }}>
                  <span style={{ color: "#6b7280" }}>Кампании:</span>
                  {segCampaignIds.map((cid) => {
                    const c = campaigns?.[String(cid)];
                    const ch = campaignChannel(c);
                    return (
                      <span key={cid} style={{ display: "inline-flex",
                                               alignItems: "center", gap: 6 }}>
                        {ch && <ChannelBadge channel={ch} size="sm" />}
                        <span>{campaignName(campaigns, cid)}</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className="form-label">Режим</label>
              <select className="form-input" value={blk.mode}
                      onChange={(e) => patch({ mode: e.target.value })}>
                <option value="EMAIL_ONLY">📧 Email</option>
                <option value="CONTACT_FORM_ONLY">📝 Контакт-форма</option>
                <option value="CALENDLY_ONLY">📅 Calendly</option>
                <option value="BOTH">✉️ Email + Contact</option>
              </select>
            </div>
          </div>

          {/* Лимит */}
          <div>
            <label className="form-label">Лимит блока</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="form-input" type="number" min={0}
                     style={{ flex: 1 }} value={blk.limit || 0}
                     onChange={(e) =>
                       patch({ limit: parseInt(e.target.value, 10) || 0 })} />
              <select className="form-input" style={{ width: 160 }}
                      value={blk.limit_mode}
                      onChange={(e) => patch({ limit_mode: e.target.value })}>
                <option value="abs">шт</option>
                <option value="segment_percent">% сегмента</option>
              </select>
            </div>
          </div>

          {/* Runtime defaults по каналам */}
          {modeChannels.length > 0 && (
            <div style={{ padding: 14, background: "#f0f9ff",
                          border: "1px solid #bae6fd", borderRadius: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#075985",
                            marginBottom: 10 }}>
                🖥 Runtime defaults для блока
              </div>
              <div style={{ display: "grid",
                            gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {modeChannels.map((ch) => {
                  const meta = _channelMeta(ch);
                  return (
                    <div key={ch}>
                      <label className="form-label">
                        {meta.icon} {meta.label}
                      </label>
                      <select className="form-input"
                              value={blk.runtime_defaults?.[ch] || ""}
                              onChange={(e) =>
                                patchRtDefaults(ch, e.target.value || null)}>
                        <option value="">— любой доступный —</option>
                        {runtimeChoices.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.kind === "android" ? "📱" : "🌐"} {r.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
                Воркеры без явного runtime будут использовать это значение.
                {" "}EMAIL + CONTACT наследует EMAIL.
              </div>
            </div>
          )}

          {/* Сводка по весам */}
          <div style={{
            padding: 12, background: pctStatusColor.bg,
            border: `1px solid ${pctStatusColor.border}`,
            borderRadius: 10, fontSize: 13, lineHeight: 1.6,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div>
                👥 Воркеров: <b>{blk.workers.length}</b>
                {" · "}Суммарный вес:{" "}
                <b style={{ color: pctStatusColor.text }}>
                  {totalPct.toFixed(1)}%
                </b>
                {" "}
                <span style={{ color: pctStatusColor.text, fontSize: 12 }}>
                  {pctStatusColor.label}
                </span>
              </div>
              {blk.workers.length > 0 && totalPct !== 100 && (
                <button className="btn-cancel"
                        style={{ fontSize: 11, padding: "3px 10px" }}
                        onClick={normalizeTo100}>
                  ⚖️ Нормализовать до 100%
                </button>
              )}
            </div>
          </div>

          {/* Воркеры */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 10,
                          flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                👥 Воркеры ({blk.workers.length} из {availableWorkers.length})
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn-cancel"
                        style={{ fontSize: 12, padding: "4px 10px" }}
                        onClick={onRefreshWorkers}>🔄</button>
                <button className="btn-primary"
                        style={{ fontSize: 12, padding: "4px 12px" }}
                        onClick={() => setBulkOpen(true)}
                        disabled={!blk.segment_id || availableWorkers.length === 0}>
                  ➕ Массово
                </button>
                <button className="btn-cancel"
                        style={{ fontSize: 12, padding: "4px 10px" }}
                        onClick={addOne}
                        disabled={!blk.segment_id || availableWorkers.length === 0}>
                  ➕ Один
                </button>
                {blk.workers.length > 0 && (
                  <button className="btn-cancel"
                          style={{ fontSize: 12, padding: "4px 10px",
                                   color: "#dc2626" }}
                          onClick={clearAll}>
                    ✕ Все
                  </button>
                )}
              </div>
            </div>

            {blk.workers.length > 0 && (
              <label style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 12px", marginBottom: 10,
                background: evenDistribution ? "#eff6ff" : "#f9fafb",
                border: `1px solid ${evenDistribution ? "#93c5fd" : "#e5e7eb"}`,
                borderRadius: 8, fontSize: 13, cursor: "pointer",
                fontWeight: evenDistribution ? 600 : 400,
                color: evenDistribution ? "#1d4ed8" : "#374151",
              }}>
                <input type="checkbox" checked={evenDistribution}
                       onChange={(e) => toggleEvenDistribution(e.target.checked)} />
                ⚖️ Распределить ровно
                <span style={{ fontSize: 11, color: "#6b7280",
                               fontWeight: 400, marginLeft: 4 }}>
                  (каждому = 100 / {blk.workers.length} = {evenPct}%)
                </span>
              </label>
            )}

            {!blk.segment_id && (
              <div style={{ color: "#9ca3af", fontSize: 13 }}>
                Сначала выберите сегмент.
              </div>
            )}

            {blk.segment_id && blk.workers.length === 0 && (
              <div style={{ padding: "16px 20px", textAlign: "center",
                            background: "#f9fafb", borderRadius: 8,
                            color: "#9ca3af", fontSize: 13 }}>
                — воркеров пока нет —
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  Нажмите «➕ Массово» для быстрого добавления
                </div>
              </div>
            )}

            {blk.workers.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={rowHeader}>
                  <span>Транспорт</span>
                  <span>Воркер</span>
                  <span style={{ textAlign: "center" }}>Вес, %</span>
                  <span style={{ textAlign: "center" }}>Лимит</span>
                  <span style={{ textAlign: "center" }}>Runtime</span>
                  <span></span>
                </div>

                {blk.workers.map((w, i) => {
                  const wObj = workerByName[w.worker_name];
                  const kind = wObj?._kind || "unknown";
                  const type = wObj?._type || w.channel || "email";
                  const kindCfg = WORKER_KIND[kind] || WORKER_KIND.unknown;
                  const typeCfg = WORKER_TYPE[type] || WORKER_TYPE.email;
                  const est = estPerWorker(w);
                  const isAvail = availableNames.has(w.worker_name);

                  const inOthers = findOtherUsages(
                    usageIndex, w.worker_name, blockIdx,
                  );

                  const rtKey = _runtimeKeyFor(w.channel);
                  const inherit = blk.runtime_defaults?.[rtKey] || null;
                  const rtValue = w.runtime_id || "";

                  return (
                    <div key={i} style={{
                      ...row,
                      background: isAvail ? "#fff" : "#fef9c3",
                    }}>
                      <div style={{
                        padding: "5px 8px", borderRadius: 6,
                        fontSize: 10, fontWeight: 700,
                        background: kindCfg.bg, color: kindCfg.color,
                        whiteSpace: "nowrap",
                        display: "inline-flex", alignItems: "center",
                        justifyContent: "center", gap: 3,
                      }} title={kindCfg.label}>
                        <span>{kindCfg.icon}</span>
                        <span style={{ fontSize: 10 }}>{kindCfg.label}</span>
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <select className="form-input"
                                style={{ width: "100%", fontSize: 12,
                                         padding: "6px 8px" }}
                                value={w.worker_name}
                                onChange={(e) =>
                                  setWorkerField(i, "worker_name", e.target.value)}>
                          {availableWorkers.map((aw) => {
                            const nm = aw._name || aw.name;
                            return <option key={nm} value={nm}>{nm}</option>;
                          })}
                          {!isAvail && (
                            <option value={w.worker_name}>
                              {w.worker_name} (не для этого канала)
                            </option>
                          )}
                        </select>
                        <div style={{ fontSize: 11, color: "#6b7280",
                                      marginTop: 3, display: "flex",
                                      gap: 10, flexWrap: "wrap" }}>
                          <span>{typeCfg.icon} {typeCfg.label}</span>
                          {wObj?._pripiska && <span>🏷 {wObj._pripiska}</span>}
                          {est != null && (
                            <span style={{ color: "#16a34a" }}>
                              ≈ {est} сообщ.
                            </span>
                          )}
                        </div>
                        {inOthers.length > 0 && (
                          <div style={{ fontSize: 11, color: "#b45309",
                                        marginTop: 3 }}>
                            📊 также в{" "}
                            {inOthers.map((u) =>
                              `#${u.blockNum} (${u.segmentName}, ${u.percent}%)`
                            ).join(", ")}
                          </div>
                        )}
                      </div>

                      <input className="form-input" type="number" min={0}
                             step={0.1}
                             style={{ width: "100%", textAlign: "center",
                                      padding: "6px 8px",
                                      background: evenDistribution
                                        ? "#f3f4f6" : "#fff",
                                      color: evenDistribution
                                        ? "#6b7280" : "#111",
                                      cursor: evenDistribution
                                        ? "not-allowed" : "text" }}
                             disabled={evenDistribution}
                             value={Number(w.percent).toFixed(2).replace(/\.?0+$/, "") || 0}
                             onChange={(e) =>
                               setWorkerField(i, "percent",
                                 parseFloat(e.target.value) || 0)} />

                      <input className="form-input" type="number" min={0}
                             style={{ width: "100%", textAlign: "center",
                                      padding: "6px 8px" }}
                             placeholder="∞"
                             value={w.limit || ""}
                             onChange={(e) =>
                               setWorkerField(i, "limit",
                                 parseInt(e.target.value, 10) || 0)} />

                      <select className="form-input"
                              style={{ width: "100%", fontSize: 11,
                                       padding: "6px 6px",
                                       fontFamily: "Consolas, monospace" }}
                              value={rtValue}
                              onChange={(e) =>
                                setWorkerField(i, "runtime_id",
                                  e.target.value || null)}>
                        <option value="">
                          ↳ {inherit || "любой"} (inherit)
                        </option>
                        {runtimeChoices.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.kind === "android" ? "📱" : "🌐"} {r.id}
                          </option>
                        ))}
                      </select>

                      <button className="btn-cancel"
                              style={{ padding: "6px 4px", color: "#dc2626",
                                       width: 32, height: 32 }}
                              onClick={() => removeWorker(i)}
                              title="Удалить воркера">✕</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onCancel}>Отмена</button>
          <button className="btn-primary"
                  onClick={() => {
                    if (!blk.segment_id) { alert("Выбери сегмент"); return; }
                    if (!blk.workers.length) {
                      alert("Добавь хотя бы одного воркера"); return;
                    }
                    onSave({
                      segment_id: blk.segment_id,
                      mode: blk.mode,
                      limit: blk.limit || 0,
                      limit_mode: blk.limit_mode || "abs",
                      cf_selection: blk.cf_selection,
                      cf_failed_action: blk.cf_failed_action,
                      fetch_mode: blk.fetch_mode,
                      runtime_defaults: blk.runtime_defaults || {},
                      workers: blk.workers,
                    });
                  }}>
            💾 Сохранить блок
          </button>
        </div>
      </div>

      {bulkOpen && (
        <BulkWorkersDialog
          availableWorkers={availableWorkers}
          alreadyInBlock={inBlockNames}
          usedInOtherBlocks={usedInOtherBlocks}
          onAdd={onBulkAdd}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// styles
// ═══════════════════════════════════════════════════════════════
const ROW_COLS = "110px minmax(0, 1fr) 90px 100px 170px 32px";

const rowHeader = {
  display: "grid", gridTemplateColumns: ROW_COLS, gap: 8,
  alignItems: "center", padding: "4px 10px",
  fontSize: 10, color: "#6b7280", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: ".04em",
  borderBottom: "1px solid #e5e7eb",
};
const row = {
  display: "grid", gridTemplateColumns: ROW_COLS, gap: 8,
  alignItems: "center", padding: "8px 10px",
  border: "1px solid #e5e7eb", borderRadius: 8,
};
const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
  backdropFilter: "blur(3px)", zIndex: 1200,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const card = {
  background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 1180, maxHeight: "92vh", display: "flex",
  flexDirection: "column", overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,.3)",
};
const header = {
  padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "space-between", alignItems: "center",
};
const body = {
  flex: 1, overflow: "auto", padding: 20,
  display: "flex", flexDirection: "column", gap: 16,
};
const footer = {
  padding: "14px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "flex-end", gap: 8,
};
const blockCard = {
  display: "flex", alignItems: "flex-start", gap: 10,
  border: "1px solid #e5e7eb", borderRadius: 10,
  padding: 12, background: "#fff",
};
const iconBtn = { padding: "4px 10px", fontSize: 12 };
const checkLabel = {
  display: "flex", gap: 6, alignItems: "center",
  fontSize: 12, color: "#374151", cursor: "pointer",
};